import { JWT, decodeJWT } from "aws-amplify/auth";
import { FeatureDisabledError } from "../../errors/FeatureDisabledError";
import AuthService from "../../services/AuthService";
import { FeatureFlagName } from "../../types/feature-flags/featureFlag";

class JwtScope {
  private readonly _customer?: string;
  private readonly _sites?: string[];

  constructor(customer?: string, sites?: string[]) {
    this._customer = customer;
    this._sites = sites;
  }

  get customer(): string | undefined {
    return this._customer;
  }

  get sites(): string[] | undefined {
    return this._sites;
  }
}

export class JwtPayload {
  private readonly _aud?: string;
  private readonly _scope?: string;
  private readonly _featureFlags?: string;

  public readonly iss?: string;
  public readonly nbf?: number;
  public readonly iat?: number;
  public readonly exp?: number;
  public readonly sub?: string;
  public readonly customerId?: string;
  public readonly roleId?: string;
  public readonly permission?: string;
  public readonly email?: string;
  public readonly userName?: string;

  constructor(jwt: JWT["payload"]) {
    this._aud = jwt.aud as string | undefined;
    this._scope = jwt.scope as string | undefined;
    this._featureFlags = jwt.featureFlags as string | undefined;

    this.iss = jwt.iss;
    this.nbf = jwt.nbf;
    this.iat = jwt.iat;
    this.exp = jwt.exp;
    this.sub = jwt.sub;
    this.customerId = jwt.customerId as string | undefined;
    this.roleId = jwt.roleId as string | undefined;
    this.permission = jwt.permission as string | undefined;
    this.email = jwt.email as string | undefined;
    this.userName = jwt.userName as string | undefined;
  }

  private hasFeature(feature: FeatureFlagName): boolean {
    const flags = this.featureFlags;
    if (!flags) {
      return false;
    }
    return flags.includes(feature);
  }

  public assertFeatureEnabled(feature: FeatureFlagName) {
    if (!this.hasFeature(feature)) {
      throw new FeatureDisabledError(feature);
    }
  }

  public get featureFlags(): FeatureFlagName[] | undefined {
    if (!this._featureFlags) {
      return undefined;
    }
    return JSON.parse(this._featureFlags);
  }

  public get scope(): JwtScope | undefined {
    if (!this._scope) {
      return undefined;
    }
    const json = JSON.parse(this._scope);
    return new JwtScope(json.customerId, json.sites);
  }

  public get aud(): string | string[] | undefined {
    if (!this._aud) {
      return undefined;
    }
    return JSON.parse(this._aud);
  }
}

export async function getDecodedToken(): Promise<JwtPayload | undefined> {
  const authService = AuthService.getInstance();
  const idToken = await authService.getIdToken();
  if (!idToken) {
    throw new Error("No token found");
  }
  const decoded = decodeJWT(idToken);
  return new JwtPayload(decoded.payload);
}
